// src/features/landing/presentation/components/CareerHistorySection.jsx
// 인재등록(구직 신청서) 경력사항의 "경력 추가" 항목 본문.
// 일반 인재등록과 설계사 채용관 인재등록(/premium/apply/planner)의 경력 항목이 다르다.
//   general: 회사명 / 재직형태 / 직무 / 직책 / 재직기간 / 주요 업무내용
//   planner: 회사명 / 영업형태(칩 다중 + 기타 직접입력) / 위촉기간 / 추가 업무내용
// 스타일: JobSeekerApplyPage 의 전역 .jsa-* / .jsa-career-* 클래스를 그대로 쓰고,
// 설계사용 칩 스타일만 이 파일의 <style>에서 추가한다 (이 컴포넌트는 JobSeekerApplyPage 안에서만 쓰인다).
import React from "react";
import { STRONG_SALES_AREAS } from "../../domain/recruitmentOptions";

/** 설계사 경력 항목 본문 (.jsa-career-fields 내부). */
export function PlannerCareerFields({ career, idx, onUpdate }) {
    const salesTypes = career.salesTypes || [];

    // 수정 모드에서 예전(일반) 형식으로 저장돼 있던 경력 항목.
    // 재직형태/직무/직책은 설계사 스키마에 대응 필드가 없어 유실되므로,
    // 읽기전용으로 보여주고 영업형태를 다시 선택하도록 안내한다.
    const isLegacy =
        career.type !== "PLANNER" &&
        !Array.isArray(career.salesTypes) &&
        Boolean(career.employmentType || career.jobRole || career.position);
    const oldSummary = [
        career.employmentType && `재직형태 ${career.employmentType}`,
        career.jobRole && `직무 ${career.jobRole}`,
        career.position && `직책 ${career.position}`,
    ].filter(Boolean).join(" · ");

    const toggleSalesType = (code) => {
        const next = salesTypes.includes(code)
            ? salesTypes.filter((c) => c !== code)
            : [...salesTypes, code];
        onUpdate(idx, "salesTypes", next);
    };

    return (
        <div className="jsa-career-fields">
            {isLegacy && (
                <div className="jsa-legacy-notice">
                    <span className="jsa-legacy-notice-title">
                        ⓘ 예전 형식으로 등록된 경력입니다. <strong>영업형태를 다시 선택</strong>해 주세요.
                    </span>
                    {oldSummary && (
                        <span className="jsa-legacy-notice-old">예전 입력: {oldSummary} (저장 시 반영되지 않습니다)</span>
                    )}
                </div>
            )}

            {/* 회사명 */}
            <div className="jsa-field">
                <label className="jsa-label">회사명</label>
                <input
                    className="jsa-input"
                    type="text"
                    placeholder="회사명을 입력하세요"
                    value={career.companyName || ""}
                    onChange={(e) => onUpdate(idx, "companyName", e.target.value)}
                />
            </div>

            {/* 영업형태 (다중 선택 + 기타 직접입력) */}
            <div className="jsa-field">
                <label className="jsa-label">영업형태</label>
                <div className="jsa-sales-chips">
                    {STRONG_SALES_AREAS.map((a) => (
                        <button
                            key={a.code}
                            type="button"
                            className={`jsa-sales-chip ${salesTypes.includes(a.code) ? "active" : ""}`}
                            onClick={() => toggleSalesType(a.code)}
                        >
                            {a.label}
                        </button>
                    ))}
                </div>
                {salesTypes.includes("ETC") && (
                    <input
                        className="jsa-input"
                        type="text"
                        style={{ marginTop: 8 }}
                        placeholder="기타 영업형태를 입력하세요"
                        value={career.salesTypesEtc || ""}
                        onChange={(e) => onUpdate(idx, "salesTypesEtc", e.target.value)}
                    />
                )}
            </div>

            {/* 위촉기간 */}
            <div className="jsa-field">
                <label className="jsa-label">위촉기간</label>
                <div className="jsa-career-period">
                    <input
                        className="jsa-input"
                        type="date"
                        value={career.startDate || ""}
                        onChange={(e) => onUpdate(idx, "startDate", e.target.value)}
                    />
                    <span className="jsa-career-period-sep">~</span>
                    <input
                        className="jsa-input"
                        type="date"
                        value={career.endDate || ""}
                        onChange={(e) => onUpdate(idx, "endDate", e.target.value)}
                        disabled={career.isCurrent}
                    />
                    <label className="jsa-career-current-label">
                        <input
                            type="checkbox"
                            checked={!!career.isCurrent}
                            onChange={(e) => onUpdate(idx, "isCurrent", e.target.checked)}
                        />
                        현재 위촉 중
                    </label>
                </div>
            </div>

            {/* 추가 업무내용 */}
            <div className="jsa-field">
                <label className="jsa-label">추가 업무내용</label>
                <textarea
                    className="jsa-textarea"
                    placeholder="상세 업무내용을 기재해주세요."
                    value={career.mainDuties || ""}
                    onChange={(e) => onUpdate(idx, "mainDuties", e.target.value)}
                    rows={3}
                    maxLength={1000}
                />
                <span className="jsa-char-count">{(career.mainDuties || "").length} / 1000</span>
            </div>

            <style>{`
                .jsa-sales-chips {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                }
                .jsa-sales-chip {
                    padding: 7px 14px;
                    border: 1.5px solid #e5ddd7;
                    border-radius: 999px;
                    background: #fff;
                    font-size: 0.82rem;
                    font-weight: 600;
                    color: #666;
                    cursor: pointer;
                    transition: all 0.15s;
                    white-space: nowrap;
                }
                .jsa-sales-chip:hover { border-color: #f7b98a; color: #f16102; }
                .jsa-sales-chip.active {
                    background: #f16102;
                    border-color: #f16102;
                    color: #fff;
                }
                .jsa-legacy-notice {
                    display: flex;
                    flex-direction: column;
                    gap: 3px;
                    padding: 10px 12px;
                    background: #fff8ef;
                    border: 1px solid #f5d9b0;
                    border-radius: 8px;
                    font-size: 0.8rem;
                    line-height: 1.5;
                }
                .jsa-legacy-notice-title { color: #a8620a; }
                .jsa-legacy-notice-title strong { font-weight: 700; }
                .jsa-legacy-notice-old { color: #999; }
            `}</style>
        </div>
    );
}
